// @flow
import React, {Fragment, FunctionComponent, PropsWithChildren, ReactNode} from 'react';
import {Chip, ListItem, ListItemAvatar, ListItemButton, ListItemText, Stack, SvgIcon} from "@mui/material";
import FormattedDate from "../Common/formatted-date";
import ChipsList from "../chips-list";
import {JsonAnimal} from "../../api/generated/rest-dto";
import {
    formatDays,
    getDurationDays,
    getPregnancyRemainingDays,
    getPregnancyState,
    PregnancyState
} from "../AnimalDashboard/Pregnancy/pregnancy-utils";
import {ReactComponent as MatingDateSvg} from "../../icons/mating_date.svg"
import {Flare} from '@mui/icons-material';
import {AnimalAvatar} from "../Common/animal-avatar";
import {SexBadge} from "../Common/sex-badge";
import {AnimalService} from "../../services/animal-service";
import {RegistryIcon} from "../../icons";
import {HerdAnimal} from "../../types";
import {PregnancyListJson} from "../../api/generated/medical-rest";
import {DateTime} from "luxon";

type AnimalListItemProps = {
    showTags?: boolean,
    onTagClick?: (tag: string) => void,
    animal: HerdAnimal,
    onItemClick?: (animal: JsonAnimal) => void,
    secondaryAction?: ReactNode,
};

type ConditionalListItemButtonProps = PropsWithChildren<{
    onClick?: () => void
}>;

const ConditionalListItemButton: FunctionComponent<ConditionalListItemButtonProps> = ({onClick, children}) => {
    return onClick ? <ListItemButton onClick={onClick}>{children}</ListItemButton> : <>{children}</>;
}

type Optional<T> = T | null | undefined

const getPregnancyChipLabel = (pregnancy: Optional<PregnancyListJson>) => {
    if (!pregnancy) {
        return '';
    }

    const pregnancyState = getPregnancyState(pregnancy);
    const remainingDays = getPregnancyRemainingDays(pregnancy);
    switch (pregnancyState) {
        case PregnancyState.MATED: return 'gedeckt, vor ' + formatDays(getDurationDays({start: pregnancy.matingDate, end: DateTime.now()}));
        case PregnancyState.PREGNANT: return remainingDays >= 0 ? `trächtig, noch ${formatDays(remainingDays)}` : `trächtig, ${formatDays(-remainingDays)} überfällig`;
        default: return '';
    }
};

const getPregnancyIcon = (pregnancyState: Optional<PregnancyState>) => {
    switch (pregnancyState) {
        case PregnancyState.MATED: return <SvgIcon><MatingDateSvg/></SvgIcon>;
        case PregnancyState.PREGNANT: return <Flare/>;
        default: return <></>;
    }
};

const AnimalListItem: FunctionComponent<AnimalListItemProps> = ({
                                                                    animal,
                                                                    onItemClick,
                                                                    onTagClick,
                                                                    showTags = false,
                                                                    secondaryAction,
                                                                }) => {

    const handleListItemClick = onItemClick ? () => onItemClick(animal) : undefined;

    const pregnancyState = animal.pregnancy && getPregnancyState(animal.pregnancy);
    const pregnancyChipLabel = getPregnancyChipLabel(animal.pregnancy);
    const name = AnimalService.getAnimalFullName(animal);
    const title = pregnancyChipLabel
        ? <>{name} <Chip label={pregnancyChipLabel} size="small" icon={getPregnancyIcon(pregnancyState)}/></>
        : name;

    return (
        <ListItem secondaryAction={secondaryAction} alignItems="flex-start" disablePadding>
            <ConditionalListItemButton onClick={handleListItemClick}>
                <ListItemAvatar>
                    <SexBadge sex={animal.sex}>
                        <AnimalAvatar animal={animal}/>
                    </SexBadge>
                </ListItemAvatar>
                <ListItemText primary={title} secondary={
                    <>
                        <div><FormattedDate sx={{textAlign: "right"}} date={animal.dateOfBirth}/></div>
                        {animal.registryNames?.length ? (
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                                title={`In ${animal.registryNames?.length} Registern registiert`}>
                                <RegistryIcon fontSize="small"/>
                                <span>{animal.registryNames?.length}</span>
                            </Stack>
                        ) : (
                            animal.registryNames === undefined ? null : <>nicht registriert</>
                        )}
                    </>
                }/>
                {showTags && <ChipsList listItems={animal.tags} onItemClick={onTagClick}/>}
            </ConditionalListItemButton>
        </ListItem>
    );
};

export default AnimalListItem